import { motion } from "framer-motion";
import { Car, Route, Sparkles, Droplets, PackageCheck, Wrench } from "lucide-react";
import { IMAGES } from "@/config";

const reveal = {
  hidden: { y: 40, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const ImageCard = ({ img, icon: Icon, title, desc, span, testid }) => (
  <motion.article
    variants={reveal}
    data-testid={testid}
    className={`group relative overflow-hidden rounded-2xl border border-white/10 min-h-[320px] flex flex-col justify-end p-8 ${span}`}
  >
    <img
      src={img}
      alt={title}
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/10" />
    <div className="relative z-10">
      <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-black/40 backdrop-blur-md text-[#0052ff]">
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="font-display text-2xl font-bold text-white">{title}</h3>
      <p className="mt-2 max-w-md text-sm text-[#cbd5e1] leading-relaxed">{desc}</p>
    </div>
  </motion.article>
);

const TextCard = ({ icon: Icon, title, desc, span, testid }) => (
  <motion.article
    variants={reveal}
    data-testid={testid}
    className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-8 transition-colors duration-300 hover:border-[#0052ff]/50 hover:bg-white/[0.05] ${span}`}
  >
    <span className="mb-6 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-[#0052ff] transition-colors duration-300 group-hover:bg-[#0052ff] group-hover:text-white">
      <Icon className="h-5 w-5" />
    </span>
    <h3 className="font-display text-xl font-bold text-white">{title}</h3>
    <p className="mt-2 text-sm text-[#94a3b8] leading-relaxed">{desc}</p>
  </motion.article>
);

export const Services = () => {
  return (
    <section
      id="leistungen"
      data-testid="services-section"
      className="relative mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32"
    >
      <motion.div
        variants={reveal}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="mb-14 max-w-2xl"
      >
        <div className="mb-4 flex items-center gap-3">
          <span className="h-px w-10 bg-[#0052ff]" />
          <span className="text-xs uppercase tracking-[0.35em] text-[#94a3b8]">
            Leistungen
          </span>
        </div>
        <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
          Alles rund ums Fahrzeug —{" "}
          <span className="text-[#94a3b8]">aus einer Hand.</span>
        </h2>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        transition={{ staggerChildren: 0.09 }}
        className="grid grid-cols-1 md:grid-cols-12 gap-5"
      >
        <ImageCard
          testid="service-fahrdienst"
          img={IMAGES.chauffeur}
          icon={Car}
          title="Fahrdienstleistungen"
          desc="Professionelle Fahrer für Ihre Kraftfahrzeuge — pünktlich, diskret und zuverlässig, wann immer Sie uns brauchen."
          span="md:col-span-7"
        />
        <ImageCard
          testid="service-ueberfuehrung"
          img={IMAGES.transfer}
          icon={Route}
          title="Fahrzeugüberführungen"
          desc="Sichere Überführung Ihres Fahrzeugs von A nach B — deutschlandweit und termingerecht."
          span="md:col-span-5"
        />

        <TextCard
          testid="service-reinigung"
          icon={Droplets}
          title="Fahrzeugreinigung"
          desc="Innen- und Außenreinigung mit Liebe zum Detail — für den perfekten ersten Eindruck."
          span="md:col-span-4"
        />
        <TextCard
          testid="service-pflege"
          icon={Sparkles}
          title="Fahrzeugpflege"
          desc="Aufbereitung, Politur und Werterhalt — damit Ihr Fahrzeug dauerhaft glänzt."
          span="md:col-span-4"
        />
        <TextCard
          testid="service-holbring"
          icon={PackageCheck}
          title="Hol- & Bringservice"
          desc="Wir holen Ihr Fahrzeug ab und bringen es zurück — bequem ohne eigenen Aufwand."
          span="md:col-span-4"
        />

        <ImageCard
          testid="service-cleaning-wide"
          img={IMAGES.cleaning}
          icon={Wrench}
          title="Zusammenhängende Dienstleistungen"
          desc="Werkstatt-Transfers, TÜV-Fahrten, Reifenwechsel-Logistik und mehr — sprechen Sie uns einfach an."
          span="md:col-span-12"
        />
      </motion.div>
    </section>
  );
};

export default Services;
